import { getTabAssignment } from '@/auth/activeAssignment';
import { clearSession } from '@/auth/clearSession';
import { isPreviewReadOnlyPayload, notifyPreviewReadOnly } from '@/auth/previewReadOnly';
import { getSessionUserId, isSignedIn } from '@/auth/session';
import { isSigningOut, markSigningOut } from '@/config/signOutState';

/**
 * Cross-tab session signals (Authorization-Plan §13.6; UI state plan phase 1).
 *
 * The session lives in each tab's memory, so the tabs of a browser tell each
 * other when it changes:
 *
 * - a ROLE SWITCH (`announceRoleChange`): a tab that hears it, or gets a 409
 *   `role_changed` from the gateway or the identity service, is BLOCKED: a
 *   blocking banner asks to reload (RoleChangedBanner) and roleAwareFetch()
 *   no longer sends writes;
 * - a SIGN-OUT (`announceSignOut`): a tab that hears it drops its session and
 *   loads the login page.
 *
 * The signal travels on BroadcastChannel 'pnet-role' (memory only, nothing is
 * stored). Only a browser without BroadcastChannel falls back to a `storage`
 * event: the key `pnet.signal` is written and removed at once and carries ids
 * only (no role or organisation name).
 *
 * TODO(§13.6): keep unsaved form input per role (out of scope of part 4).
 */

export const ROLE_CHANNEL = 'pnet-role';
/** Fallback signal key, used ONLY when BroadcastChannel is unavailable. */
export const SIGNAL_FALLBACK_KEY = 'pnet.signal';
export const ROLE_CHANGED = 'role_changed';

export interface RoleChangeMessage {
  type: 'role-changed';
  userId: number | null;
  assignmentId: number | null;
  roleName: string | null;
  contextName: string | null;
  at: number;
}

export interface SignOutMessage {
  type: 'signed-out';
  userId: number | null;
  at: number;
}

export type SessionSignal = RoleChangeMessage | SignOutMessage;

export interface RoleChangedState {
  blocked: boolean;
  assignmentId: number | null;
  roleName: string | null;
  contextName: string | null;
}

const initialState: RoleChangedState = {
  blocked: false,
  assignmentId: null,
  roleName: null,
  contextName: null,
};

let state: RoleChangedState = initialState;
const listeners = new Set<(s: RoleChangedState) => void>();

export const getRoleChangedState = (): RoleChangedState => state;

export const subscribeRoleChanged = (listener: (s: RoleChangedState) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const publish = (next: RoleChangedState) => {
  state = next;
  listeners.forEach((listener) => listener(state));
};

/** True once this tab learnt that the role changed: writes are no longer sent. */
export const isRoleChangeBlocked = (): boolean => state.blocked;

let switchingHere = false;

/**
 * This tab is switching role itself (SidebarRoleMenu): a late 409 of a request
 * sent with the previous id must not block it while it reloads.
 */
export const markSwitchingHere = (value = true): void => {
  switchingHere = value;
};

/**
 * Block this tab. A later call may add the role name (the 409 only carries the
 * id; the name is looked up afterwards).
 */
export const markRoleChanged = (info: Partial<Omit<RoleChangedState, 'blocked'>> = {}): void => {
  if (switchingHere) return;
  publish({
    blocked: true,
    assignmentId: info.assignmentId ?? state.assignmentId,
    roleName: info.roleName ?? state.roleName,
    contextName: info.contextName ?? state.contextName,
  });
};

/** Test helper. */
export const resetRoleChangedForTests = (): void => {
  switchingHere = false;
  publish(initialState);
};

/** A 409 body from pnet.assignment-guard / AssignmentGuard. */
export const isRoleChangedPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as Record<string, unknown>;
  return p.reason === ROLE_CHANGED || p.message === ROLE_CHANGED;
};

// ---- announcing and listening -------------------------------------------------------

const hasBroadcastChannel = (): boolean => typeof BroadcastChannel !== 'undefined';

/** What the storage fallback carries: the type and ids only. */
const minimalSignal = (signal: SessionSignal): Record<string, unknown> =>
  signal.type === 'role-changed'
    ? {
        type: signal.type,
        userId: signal.userId,
        assignmentId: signal.assignmentId,
        at: signal.at,
      }
    : { type: signal.type, userId: signal.userId, at: signal.at };

const postSignal = (signal: SessionSignal): void => {
  if (hasBroadcastChannel()) {
    try {
      const channel = new BroadcastChannel(ROLE_CHANNEL);
      channel.postMessage(signal);
      channel.close();
      return;
    } catch {
      /* fall through to the storage signal */
    }
  }
  try {
    // other tabs get a `storage` event for the write; nothing stays stored
    localStorage.setItem(SIGNAL_FALLBACK_KEY, JSON.stringify(minimalSignal(signal)));
    localStorage.removeItem(SIGNAL_FALLBACK_KEY);
  } catch {
    /* storage unavailable */
  }
};

/** Tell the other tabs of this browser that the user switched role. */
export function announceRoleChange(message: Omit<RoleChangeMessage, 'type' | 'at'>): void {
  postSignal({ type: 'role-changed', at: Date.now(), ...message });
}

/** Tell the other tabs of this browser that the user signed out. */
export function announceSignOut(userId: unknown = getSessionUserId()): void {
  const id = Number(userId);
  postSignal({
    type: 'signed-out',
    userId: Number.isInteger(id) && id > 0 ? id : null,
    at: Date.now(),
  });
}

/**
 * Whether a message concerns this tab: same user, and an assignment other
 * than the one this tab runs with (a tab already on the new role ignores it).
 */
export const concernsThisTab = (
  message: Partial<RoleChangeMessage> | null,
  currentUserId: number | null,
  tabAssignmentId: number | null,
): boolean => {
  if (!message || message.type !== 'role-changed') return false;
  if (currentUserId !== null && message.userId != null && message.userId !== currentUserId)
    return false;
  if (tabAssignmentId !== null && message.assignmentId === tabAssignmentId) return false;
  return true;
};

/** Whether a sign-out in another tab ends this tab's session: signed in here, as that user. */
export const signOutConcernsThisTab = (
  message: Partial<SignOutMessage> | null,
  currentUserId: number | null,
): boolean => {
  if (!message || message.type !== 'signed-out') return false;
  if (currentUserId === null) return false;
  return message.userId == null || message.userId === currentUserId;
};

let leavePage = (): void => {
  window.location.replace('/');
};

/** Test helper: what "load the login page" does. */
export const setLeavePageForTests = (fn: (() => void) | null): void => {
  leavePage =
    fn ??
    (() => {
      window.location.replace('/');
    });
};

const onMessage = (message: Partial<SessionSignal> | null) => {
  if (message?.type === 'signed-out') {
    if (isSigningOut()) return; // this tab is the one signing out
    if (!isSignedIn()) return; // login page: nothing to end
    if (!signOutConcernsThisTab(message, getSessionUserId())) return;
    // as the tab that signed out: session and role state dropped, then a page load
    markSigningOut();
    clearSession('signout');
    leavePage();
    return;
  }
  if (!getTabAssignment()) return; // this tab has no role yet (login page, booting)
  const change = message as Partial<RoleChangeMessage> | null;
  if (!concernsThisTab(change, getSessionUserId(), getTabAssignment()?.id ?? null)) return;
  markRoleChanged({
    assignmentId: change?.assignmentId ?? null,
    roleName: change?.roleName ?? null,
    contextName: change?.contextName ?? null,
  });
};

let listening = false;

/**
 * Start listening (once per page): BroadcastChannel, or the `storage` event
 * only when the browser has no BroadcastChannel.
 */
export function listenForSessionSignals(): () => void {
  if (listening || typeof window === 'undefined') return () => undefined;
  listening = true;
  let channel: BroadcastChannel | null = null;
  try {
    if (hasBroadcastChannel()) {
      channel = new BroadcastChannel(ROLE_CHANNEL);
      channel.onmessage = (event: MessageEvent) => onMessage(event.data);
    }
  } catch {
    channel = null;
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key !== SIGNAL_FALLBACK_KEY || !event.newValue) return;
    try {
      onMessage(JSON.parse(event.newValue));
    } catch {
      /* not ours */
    }
  };
  if (!channel) window.addEventListener('storage', onStorage);
  return () => {
    listening = false;
    channel?.close();
    window.removeEventListener('storage', onStorage);
  };
}

/** The same listener under its earlier name (RoleChangedBanner). */
export const listenForRoleChanges = listenForSessionSignals;

// ---- fetch -----------------------------------------------------------------------------

const READ_METHODS = ['GET', 'HEAD', 'OPTIONS'];

const roleChangedResponse = (): Response =>
  new Response(
    JSON.stringify({
      success: false,
      message: ROLE_CHANGED,
      reason: ROLE_CHANGED,
      active_assignment_id: state.assignmentId,
    }),
    { status: 409, headers: { 'Content-Type': 'application/json' } },
  );

/**
 * fetch() for the gateway and the identity service:
 * - once the tab is blocked, writes (anything but GET/HEAD/OPTIONS) are not
 *   sent; the caller gets the same 409 body the servers send;
 * - a 409 `role_changed` answer blocks the tab;
 * - a 403 `preview_read_only` (a write during a role preview) shows
 *   "Preview is read only".
 */
export async function roleAwareFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const method = String(init.method ?? 'GET').toUpperCase();
  if (state.blocked && !READ_METHODS.includes(method)) {
    return roleChangedResponse();
  }
  const response = await fetch(input, init);
  if (response.status === 403) {
    try {
      // role preview: every write is refused (Authorization-Plan §13.7)
      if (isPreviewReadOnlyPayload(await response.clone().json())) notifyPreviewReadOnly();
    } catch {
      /* not JSON: an ordinary 403 */
    }
  }
  if (response.status === 409) {
    try {
      const payload = await response.clone().json();
      if (isRoleChangedPayload(payload)) {
        const id = Number(payload?.active_assignment_id);
        markRoleChanged({ assignmentId: Number.isInteger(id) && id > 0 ? id : null });
      }
    } catch {
      /* not JSON: an ordinary 409 */
    }
  }
  return response;
}
